import { useEffect, useRef, useState, Fragment, type ReactNode } from 'react'
import {
  ParrotIcon,
  CombatIcon,
  ScopeIcon,
  WatchtowerIcon,
  ScrollIcon,
  LanternIcon,
  CabineIcon,
  ChevronDown,
} from '@/components/game/GameIcons'
import { ResourceTally } from '@/components/game/ui'
import { formatTimeAgo } from '@/lib/time'
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type NotificationDto,
} from '@/api/hooks'

/**
 * Header bell + dropdown panel: the persistent "digest pendant ton absence".
 * Shows EVERY notification type — mission completions, raid outcomes, and quest
 * events (quest_offered when a sea find drops a fresh offer, quest_ready when a
 * reward is claimable). There is no type filter anywhere (bell, hook or API):
 * the list endpoint returns all non-archived rows newest-first, and the Quêtes
 * page is a complementary surface, not a substitute. Badge is driven live by
 * the /users/{id}/notifications Mercure topic (subscribed in AppLayout).
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  // Accordion: at most one row expanded at a time, so the panel stays compact.
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { data } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const unread = data?.unread ?? 0
  const items = data?.notifications ?? []

  // Click-outside-to-close. `mousedown` fires before `click`, so a tap on the
  // parrot toggle still reaches the button without this listener closing first
  // (the button is inside containerRef, so it's never treated as "outside").
  // The mobile backdrop is also inside containerRef and closes via its own
  // handler — this listener covers the desktop dropdown case.
  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  // Collapse any open row when the panel closes, so reopening starts clean.
  useEffect(() => {
    if (!open) setExpandedId(null)
  }, [open])

  return (
    // xl:shrink-0 : la cloche vit dans le cluster de droite de la barre globale,
    // où le flex la rabotait de 36 à 29 px sur la tranche serrée. Bornée à xl,
    // comme les autres emblèmes, parce qu'en dessous ce cluster a besoin de sa
    // souplesse pour tenir sur un écran de 320 px.
    <div className="relative xl:shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread > 0 ? ` (${unread} non lues)` : ''}`}
        className="relative grid place-items-center w-9 h-9 rounded-md text-ocean-100 hover:text-sand-200 hover:bg-ocean-700/40 transition-colors"
      >
        <ParrotIcon className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 grid place-items-center rounded-full bg-rum-500 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile-only backdrop: dims the page below the header and dismisses on
              tap. Sized with explicit dvh (not inset-0) because the blurred header
              is the containing block for our fixed children — inset-bottom would
              clip to the 64px bar. Hidden from `sm` up (desktop uses the dropdown). */}
          <div
            className="fixed inset-x-0 top-16 h-[calc(100dvh-4rem)] z-30 bg-ocean-900/70 sm:hidden"
            aria-hidden="true"
            onMouseDown={() => setOpen(false)}
          />
          {/* Full-width sheet on mobile (fixed, below the header), anchored
              dropdown on desktop (sm:absolute under the bell). */}
          <div className="fixed left-2 right-2 top-[4.5rem] z-40 max-h-[calc(100dvh-5.5rem)] overflow-y-auto rounded-lg border border-sand-500/20 bg-ocean-900/95 backdrop-blur-md shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-h-96">
            <div className="flex items-center justify-between px-3 py-2 border-b border-sand-500/15">
              <span className="text-sm font-display text-sand-300">Notifications</span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  className="text-xs text-ocean-200 hover:text-sand-300 transition-colors"
                >
                  Tout marquer lu
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-ocean-300">Rien à signaler, capitaine.</p>
            ) : (
              <ul className="divide-y divide-sand-500/10">
                {items.map((n) => (
                  <NotificationRow
                    key={n.id}
                    n={n}
                    expanded={expandedId === n.id}
                    onToggle={() => setExpandedId((cur) => (cur === n.id ? null : n.id))}
                    onRead={() => markRead.mutate(n.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * One row. Collapsed it's a scannable digest (title + 2-line clamp). A tap
 * expands the full body in place AND marks it read in a single gesture (reading
 * = read); re-tapping just collapses. Rows without a body (scout pings) have no
 * chevron and simply mark read on tap — the original behaviour.
 */
function NotificationRow({
  n,
  expanded,
  onToggle,
  onRead,
}: {
  n: NotificationDto
  expanded: boolean
  onToggle: () => void
  onRead: () => void
}) {
  const { icon, title, body } = presentNotification(n)
  const hasBody = Boolean(body)

  const handleClick = () => {
    if (!n.read) onRead()
    if (hasBody) onToggle()
  }

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        aria-expanded={hasBody ? expanded : undefined}
        className={`w-full text-left px-3 py-2.5 hover:bg-ocean-700/40 transition-colors ${n.read ? 'opacity-60' : ''}`}
      >
        <p className="text-sm text-sand-200 flex items-center gap-2">
          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-rum-500 shrink-0" aria-hidden="true" />}
          {icon}
          <span className="flex-1 min-w-0">{title}</span>
          {hasBody && (
            <ChevronDown
              className={`w-3.5 h-3.5 text-ocean-300 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          )}
        </p>
        {hasBody && (
          <div className={`mt-0.5 text-xs text-ocean-200 ${expanded ? '' : 'line-clamp-2'}`}>{body}</div>
        )}
        {expanded && (
          <p className="mt-1.5 text-[11px] text-ocean-300 flex items-center gap-1.5">
            <span>{formatTimeAgo(n.createdAt)}</span>
            {n.read && <span className="text-ocean-300">✓ lu</span>}
          </p>
        )}
      </button>
    </li>
  )
}

type LootBag = { gold: number; wood: number; rhum: number }
type Salvage = { gold: number; wood: number }

// A URL inside a server-authored body (changelog / broadcast notices) becomes an
// actionable link. The whole row is a <button>, so a real <a> would be invalid
// DOM nesting (React warns); a role="link" span with window.open gives the same
// affordance as valid phrasing content. stopPropagation so following the link
// doesn't also toggle / mark-read the row it sits in.
const URL_RE = /(https?:\/\/[^\s]+)/g

function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

function linkify(text: string): ReactNode {
  return text.split(URL_RE).map((segment, i) => {
    // split() with one capture group alternates: even = prose, odd = matched URL.
    if (i % 2 === 0) return segment
    // Peel trailing sentence punctuation so "…/x." links to "…/x", not "…/x.".
    const trail = segment.match(/[.,;:!?)\]]+$/)?.[0] ?? ''
    const url = trail ? segment.slice(0, -trail.length) : segment
    return (
      <Fragment key={i}>
        <span
          role="link"
          tabIndex={0}
          className="text-sand-300 underline underline-offset-2 hover:text-sand-200 break-all cursor-pointer"
          onClick={(e) => {
            e.stopPropagation()
            openInNewTab(url)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              openInNewTab(url)
            }
          }}
        >
          {url}
        </span>
        {trail}
      </Fragment>
    )
  })
}

/**
 * Type-aware presentation: icon + title + body for one notification. Most types
 * surface the server-rendered title/body verbatim (the generic fallback). The
 * two PvP types (M10) are re-framed client-side from their `payload` so the bell
 * can speak in the viewer's voice (role-aware) and show loot/salvage as ICONS
 * rather than the server's plain-text body (icons-not-words rule).
 */
function presentNotification(n: NotificationDto): { icon: ReactNode; title: string; body: ReactNode } {
  const p = n.payload as Record<string, unknown>

  if (n.type === 'pvp_raid_resolved') {
    const role = p.role === 'defender' ? 'defender' : 'attacker'
    const outcome = typeof p.outcome === 'string' ? p.outcome : null
    const opponent = typeof p.opponentPseudo === 'string' ? p.opponentPseudo : 'un capitaine'
    const plundered = outcome === 'plundered'
    // Troisième dénouement : la flotte est arrivée sur une cible qu'un AUTRE
    // venait de piller, donc elle a fait demi-tour sans se battre. À traiter avant
    // le binaire gagné/perdu, qui annoncerait sinon une défaite ou un repel qui
    // n'ont jamais eu lieu.
    const immune = outcome === 'immune'
    const loot = isLoot(p.loot) ? p.loot : null
    const salvage = isSalvage(p.salvage) ? p.salvage : null

    const title = immune
      ? role === 'attacker'
        ? `Ton raid sur ${opponent} : demi-tour, cible immunisée`
        : `${opponent} a fait demi-tour devant ton immunité`
      : role === 'attacker'
        ? `Ton raid sur ${opponent} : ${plundered ? 'gagné' : 'perdu'}`
        : plundered
          ? `Ton île a été pillée par ${opponent}`
          : `Tu as repoussé ${opponent}`

    let body: ReactNode = undefined

    if (plundered && loot && (loot.gold > 0 || loot.wood > 0 || loot.rhum > 0)) {
      body = (
        <span className="inline-flex items-center gap-1.5">
          <span>{role === 'attacker' ? 'Butin' : 'Pillé'}</span>
          <ResourceTally gold={loot.gold} wood={loot.wood} rhum={loot.rhum} />
        </span>
      )
      // Un demi-tour ne rapporte pas d'épaves : le renflouement se gagne en tenant
      // la ligne, pas en n'étant pas attaqué.
    } else if (!plundered && !immune && role === 'defender' && salvage && (salvage.gold > 0 || salvage.wood > 0)) {
      body = (
        <span className="inline-flex items-center gap-1.5">
          <span>Récupéré</span>
          <ResourceTally gold={salvage.gold} wood={salvage.wood} />
        </span>
      )
    }

    return { icon: <CombatIcon className="w-3.5 h-3.5 text-rum-400 shrink-0" aria-hidden="true" />, title, body }
  }

  if (n.type === 'island_scouted') {
    const scout = typeof p.scoutPseudo === 'string' ? p.scoutPseudo : 'Un capitaine'
    return {
      icon: <ScopeIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" aria-hidden="true" />,
      title: `${scout} a observé tes défenses`,
      body: undefined,
    }
  }

  if (n.type === 'scout_repelled') {
    const scout = typeof p.scoutPseudo === 'string' ? p.scoutPseudo : 'Un capitaine'
    return {
      icon: <WatchtowerIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" aria-hidden="true" />,
      title: `${scout} a tenté de relever tes défenses ; ta tour de guet l'a repoussé`,
      body: undefined,
    }
  }

  if (n.type === 'changelog') {
    return {
      icon: <ScrollIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" aria-hidden="true" />,
      title: n.title,
      body: n.body ? <span className="whitespace-pre-line">{linkify(n.body)}</span> : undefined,
    }
  }

  // Carte au trésor (intro, palier, escale consommée) — texte serveur, lanterne maison.
  if (n.type === 'streak_started' || n.type === 'streak_milestone' || n.type === 'streak_shore_leave') {
    return {
      icon: <LanternIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" aria-hidden="true" />,
      title: n.title,
      body: n.body || undefined,
    }
  }

  // Déblocage d'items de cabine (portrait/blason/titre) — texte serveur groupé,
  // écusson-buste maison (le même que la section cabine du Codex).
  if (n.type === 'cosmetic_unlocked') {
    return {
      icon: <CabineIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" aria-hidden="true" />,
      title: n.title,
      body: n.body || undefined,
    }
  }

  // Palier du Grand Chantier érigé. Le fanal du Grand Phare, littéralement.
  if (n.type === 'chantier_tier_erected') {
    return {
      icon: <LanternIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" aria-hidden="true" />,
      title: n.title,
      body: n.body || undefined,
    }
  }

  // Generic fallback for every other (server-rendered) type.
  return { icon: null, title: n.title, body: n.body || undefined }
}

function isLoot(v: unknown): v is LootBag {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as LootBag).gold === 'number' &&
    typeof (v as LootBag).wood === 'number' &&
    typeof (v as LootBag).rhum === 'number'
  )
}

function isSalvage(v: unknown): v is Salvage {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Salvage).gold === 'number' &&
    typeof (v as Salvage).wood === 'number'
  )
}
